/* This component is used to animate the transition between different components on the same page when they
have different background colors. The component can be added on a given cshtml template, then 
make sure the parent div of that page has the id set to 'animated-background-page-container' and it will 
automatically animate the transition between those components that have a custom background color set using
the 'data-background-color' attribute. In order for the transition to properly work, we need to make sure
the given components do not have any other background color set. This component also supports the transition
between light background and dark background components on the same page. In order to do this and use the 
existing dark mode styling we have for different components and in order to make sure everything transitions in sync,
we had to add the same transition details to those nested elements as well. */

import useClientPathname from '@utils/hooks/useClientPathname';
import throttle from 'lodash.throttle';
import { useEffect, useRef } from 'react';

const PageBackgroundAnimator = () => {
  const currentBackgroundColor = useRef('');
  const removeElementsTransitionTimeoutId =
    useRef<ReturnType<typeof setTimeout>>();
  const currentElementMutationObserver = useRef<MutationObserver | null>(null);
  const currentCarouselShadowElements = useRef<HTMLSpanElement[]>([]);
  const initialElementsWithBackground = useRef<HTMLElement[]>([]);
  const pathname = useClientPathname();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional Missing Dependencies
  useEffect(() => {
    const timeout = setTimeout(() => {
      const pageRootElement = document.getElementById(
        'animated-background-page-container'
      );
      if (!pageRootElement) return () => ({});
      pageRootElement.style.transition = 'background-color 1s ease';

      const elementsWithBackground = Array.from(
        document.querySelectorAll<HTMLElement>('div[data-background-color]')
      ).filter((element) => element.getBoundingClientRect().height > 0);

      if (elementsWithBackground.length === 0) return () => ({});

      initialElementsWithBackground.current = elementsWithBackground;

      elementsWithBackground.forEach((element, index) => {
        const previousElement =
          index > 0 ? elementsWithBackground[index - 1] : null;
        const currentBackgroundColor = element.getAttribute(
          'data-background-color'
        );
        const previousBackgroundColor = previousElement?.getAttribute(
          'data-background-color'
        );

        if (
          previousBackgroundColor &&
          currentBackgroundColor !== previousBackgroundColor
        )
          element.classList.add('fade-up');

        const sections = element.querySelectorAll('section');
        sections.forEach((section) => {
          section.style.backgroundColor = 'transparent';
        });
      });

      const toggleElementsTransition = (isSet: boolean) => {
        const elementsWithDarkMode = Array.from(
          document.querySelectorAll<HTMLElement>('div[data-background-color] *')
        ).filter((element) =>
          Array.from(element.classList).some(
            (cl) =>
              cl.includes('dark:bg-') ||
              cl.includes('dark:text-') ||
              cl.includes('dark:border-')
          )
        );
        elementsWithDarkMode.forEach((element) => {
          element.style.transition = isSet
            ? 'background-color 1s ease, color 1s ease, border-color 1s ease'
            : '';
        });
      };

      const handleRemoveElementsTransition = () => {
        clearTimeout(removeElementsTransitionTimeoutId.current);

        removeElementsTransitionTimeoutId.current = setTimeout(() => {
          toggleElementsTransition(false);
        }, 1300);
      };

      const switchToDarkTheme = () => {
        toggleElementsTransition(true);
        pageRootElement.classList.add('dark');
        handleRemoveElementsTransition();
      };

      const switchToLightTheme = () => {
        toggleElementsTransition(true);
        pageRootElement.classList.remove('dark');
        handleRemoveElementsTransition();
      };

      const updateCarouselShadowSpan = (
        element: HTMLElement,
        newBackgroundColour: string
      ) => {
        if (element instanceof HTMLSpanElement) {
          const classList = Array.from(element.classList);
          const hasFromClass = classList.some((cls) => cls.startsWith('from-'));

          if (hasFromClass) {
            const gradientDirection = (() => {
              if (classList.includes('bg-gradient-to-l')) return 'to left';
              if (classList.includes('bg-gradient-to-r')) return 'to right';
              return '';
            })();

            if (gradientDirection) {
              element.style.backgroundImage = `linear-gradient(${gradientDirection}, ${newBackgroundColour}, transparent)`;
              currentCarouselShadowElements.current = [
                ...currentCarouselShadowElements.current,
                element,
              ];
            }
          }
        }
      };

      const handleBackgroundChange = () => {
        // We reset the background-color transition in case it was disabled by the back to top button
        if (pageRootElement.style.transition === '' && window.scrollY === 0) {
          pageRootElement.style.transition = 'background-color 1s ease';
        }

        const activeElementThreshold = (4 * window.innerHeight) / 6;
        const elementsWithBackground = initialElementsWithBackground.current;

        elementsWithBackground.forEach((element) => {
          const elementTop = element.getBoundingClientRect().top - 200;

          if (elementTop < activeElementThreshold) {
            element.classList.add('active');
          }
          if (elementTop >= window.innerHeight) {
            element.classList.remove('active');
          }
        });

        const lastElementWithBackground = elementsWithBackground.findLast(
          (element) =>
            element.getBoundingClientRect().top < activeElementThreshold
        );

        const newBackgroundColour =
          lastElementWithBackground?.getAttribute('data-background-color') ||
          '';

        requestAnimationFrame(() => {
          if (newBackgroundColour !== currentBackgroundColor.current) {
            pageRootElement.style.backgroundColor = newBackgroundColour || '';
            currentBackgroundColor.current = newBackgroundColour;

            // Reset the observer of the previous element and remove the carousels shadows gradients
            if (currentElementMutationObserver.current) {
              currentElementMutationObserver.current.disconnect();
              currentCarouselShadowElements.current?.forEach((element) => {
                element.style.backgroundImage = '';
              });
              currentCarouselShadowElements.current = [];
            }

            // We update here the colors of the carousels shadows on mobile so they match the new background color.
            // Since they can be added at a later time, after the user swiped, we need to use mutation observer,
            // but also make sure we update the ones that are present from the beginning.
            currentElementMutationObserver.current = new MutationObserver(
              (mutationsList) => {
                mutationsList.forEach((mutation) => {
                  mutation.addedNodes.forEach((node) => {
                    updateCarouselShadowSpan(
                      node as HTMLElement,
                      newBackgroundColour
                    );
                  });
                });
              }
            );

            if (lastElementWithBackground) {
              const existingShadowSpans =
                lastElementWithBackground.querySelectorAll('span');
              existingShadowSpans.forEach((span) => {
                updateCarouselShadowSpan(span, newBackgroundColour);
              });

              currentElementMutationObserver.current.observe(
                lastElementWithBackground,
                {
                  childList: true,
                  subtree: true,
                }
              );
            }

            if (newBackgroundColour === '#0A0203') {
              switchToDarkTheme();
            } else if (pageRootElement.classList.contains('dark')) {
              switchToLightTheme();
            }
          }
        });
      };

      handleBackgroundChange();

      const throttledHandleBackgroundChange = throttle(
        handleBackgroundChange,
        100
      );
      window.addEventListener('scroll', throttledHandleBackgroundChange, {
        passive: true,
      });
      window.addEventListener('resize', throttledHandleBackgroundChange, {
        passive: true,
      });

      return () => {
        throttledHandleBackgroundChange.cancel();
        window.removeEventListener('scroll', throttledHandleBackgroundChange);
        window.removeEventListener('resize', throttledHandleBackgroundChange);
      };
    }, 100);

    return () => clearTimeout(timeout);
  }, [pathname]);

  return null;
};

export default PageBackgroundAnimator;
